import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import TotalRowTable from "./TotalRowTable";
import isGHMC from "../../Common/helper/helpar";
import { BorderBottom } from "@mui/icons-material";

function CustomDataTable({
  columns,
  data,
  isLoading,
  totalZoneRow,
  showTotal = false,
}) {
  const rowsPerPage = 100;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPagesCount, setTotalPagesCount] = useState(1);
  const [sortConfig, setSortConfig] = useState({ column: null, order: "asc" });
  const [sortedData, setSortedData] = useState([]);
  const isProd = isGHMC();

  useEffect(() => {
    if (data && data.length > 0) {
      const calculatedTotalPages = Math.ceil(data.length / rowsPerPage);
      setTotalPagesCount(calculatedTotalPages);
      setCurrentPage(1);
      setSortedData(data);
    }
  }, [data, rowsPerPage]);

  useEffect(() => {
    if (sortConfig.column) {
      const totalRow = data[data.length - 1]; // Assuming the last row is the total row
      const dataWithoutTotal = data.slice(
        0,
        showTotal ? data.length - 1 : data.length
      );

      const sortedDataWithoutTotal = dataWithoutTotal.sort((a, b) => {
        const aValue = sortConfig.column(a);
        const bValue = sortConfig.column(b);

        if (aValue < bValue) {
          return sortConfig.order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.order === "asc" ? 1 : -1;
        }
        return 0;
      });

      setSortedData([...sortedDataWithoutTotal, totalRow]);
    } else {
      const currentData = data?.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );

      const dataWithoutTotal = currentData?.slice(
        0,
        showTotal ? data.length - 1 : data.length
      );

      setSortedData([...dataWithoutTotal]);
    }
  }, [sortConfig, data, currentPage]);
  const handlePageChange = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPagesCount));
    setCurrentPage(newPage);
  };

 
  const handleSort = (selector) => {
    let order = "asc";
    if (sortConfig.column === selector && sortConfig.order === "asc") {
      order = "desc";
    }
    setSortConfig({ column: selector, order });

    const totalRow = sortedData[sortedData.length - 1];
    const dataWithoutTotal = sortedData.slice(0, sortedData.length - 1);

    const sortedDataWithoutTotal = dataWithoutTotal.sort((a, b) => {
      if (a[selector] < b[selector]) return order === "asc" ? -1 : 1;
      if (a[selector] > b[selector]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortedData([...sortedDataWithoutTotal, totalRow]);
  };

  const customStyles = {
    rows: {
      style: {
        backgroundColor: "#8fc9ff",
        "&:nth-child(odd)": {
          // Odd rows styling
          backgroundColor: isProd ? "#8fc9ff" : "#F8CCCD",
          border: "0",
        },
        "&:nth-child(even)": {
          // Even rows styling
          backgroundColor: isProd ? "#aed8ff" : "rgb(255, 237, 238)",
          border: "0",
        },
      },
      highlightOnHoverStyle: {
        backgroundColor: "inherit",
        boxShadow: "none",
        transform: "none",
      },
    },
    headCells: {
      style: {
        paddingRight: "8px",
        color: "#fff",
        backgroundColor: isProd ? "#0d5da5" : "#EC1117",
        fontWeight: "600",
        borderLeft: "1px solid #FFFFFF",
        borderBottom: "1px solid #FFFFFF",
        fontSize: "14px",
        paddingLeft: "10px",
        fontFamily: "poppins",
        flexWrap: "wrap",
        wordWrap: "break-word",
        width: "180px",
        justifyContent: "center",
      },
    },
    cells: {
      style: {
        borderLeft: "1px solid #FFFFFF",
        borderBottom: "1px solid #FFFFFF",
        padding: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
        BorderBottom: "none",
        borderLeft: "1px solid #FFFFFF",
        borderBottom: "1px solid #FFFFFF",
        textAlign: "center",
        justifyContent: "center",
        'div:first-child' : {
          whiteSpace: "wrap !important",
        }
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row, index) => index % 2 === 0, // Even rows
      style: {
        backgroundColor: isProd ? "#8fc9ff" : "#F8CCCD", // Color for even rows
      },
    },
    {
      when: (row, index) => index % 2 !== 0, // Odd rows
      style: {
        backgroundColor: isProd ? "#aed8ff" : "rgb(255, 237, 238)", // Color for odd rows
      },
    },
    {
      when: (row) => row && (row.zoneName === "Total" || row.circleName === "Total"), // Total row
      style: {
        color: "black",
      },
    },
  ];

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const calculateSerialNumber = (index, row) => {
    // If row is undefined or null, return empty string (prevent errors)
    if (!row) return "";

    // If zoneName or circleName is "Total", return empty string
    if (row.zoneName === "Total" || row.circleName === "Total") {
      return "";  // Empty serial number for 'Total' rows
    }

    // Otherwise, calculate and return the serial number for regular rows
    return (currentPage - 1) * rowsPerPage + index + 1;
  };

  const currentPageData =
    data &&
    data
      .filter((row) => row.zoneName !== "Total" && row.circleName !== "Total") // Exclude "Total" row for both zoneName and circleName
      .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

      const totalRow = data?.find((row) => row.zoneName === "Total" || row.circleName === "Total");
      const finalData = currentPageData?.concat(
        currentPage === totalPagesCount && totalRow ? [totalRow] : []
      );
  //  const currentPageData = data && data

  //const totalRow = data && data[data.length - 1];

  const convertToHeaderFormat = (key) => {
    const formattedKey = key.replace(/([A-Z])/g, " $1").trim();
    return formattedKey.charAt(0).toUpperCase() + formattedKey.slice(1);
  };

  const renderTableHeaders = () => {
    return columns.map((column, index) => (
      <th
        key={column.selector || index}
        onClick={() => handleSort(column.selector)}
      >
        <div>
          {index === 0 ? (
            <span>Serial Number</span>
          ) : (
            <>
              <span>{convertToHeaderFormat(column.name)}</span>
              {column.sortable && (
                <span>
                  {sortConfig.column === column.selector &&
                    sortConfig.order && (
                      <span>{sortConfig.order === "asc" ? " 🔼i" : " 🔽"}</span>
                    )}
                </span>
              )}
            </>
          )}
        </div>
      </th>
    ));
  };

  const maxPageNumbersToShow = 5;

  const generatePageNumbers = () => {
    const pages = [];
    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxPageNumbersToShow / 2)
    );
    let endPage = Math.min(
      totalPagesCount,
      startPage + maxPageNumbersToShow - 1
    );

    // Adjust start and end if they are out of bounds
    if (endPage - startPage < maxPageNumbersToShow - 1) {
      startPage = Math.max(1, endPage - maxPageNumbersToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div style={{ position: "relative", zIndex: 0 }}>
      <div>
        <div>
          <DataTable
            fixedHeader
            fixedHeaderScrollHeight="460px"
            columns={[
              {
                ...columns[0], // Spread existing column properties
                format: (row, index) => {
                  // Call calculateSerialNumber and pass `row` and `index`
                  return (row.zoneName === "Total" || row.circleName === "Total" || row.ownerTypeName === "Total")
                    ? ""  // Return empty string for 'Total' rows
                    : calculateSerialNumber(index, row);  // Otherwise, calculate serial number
                },
              },
              ...columns.slice(1), // Rest of the columns remain the same
            ]}
            data={finalData}  // Use finalData
            customStyles={customStyles}
            noDataComponent={isLoading ? "Loading..." : "No data available"}
            conditionalRowStyles={conditionalRowStyles}
            paginationPerPage={rowsPerPage}
            // persistTableHead
            persistTableHead={true}
          />
        </div>
      </div>
      {data && data.length > rowsPerPage && (
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Centering the pagination
            padding: "8px",
          }}
        >
          <button
            style={{
              backgroundColor: "#f0f0f0",
              color: "#000",
              borderRadius: "4px",
              border: "1px solid #ccc",
              padding: "8px 12px",
              marginRight: "5px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            First
          </button>

          <button
            style={{
              backgroundColor: "#f0f0f0",
              color: "#000",
              borderRadius: "4px",
              border: "1px solid #ccc",
              padding: "8px 12px",
              marginRight: "5px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {generatePageNumbers().map((pageNumber) => (
            <button
              key={pageNumber}
              style={{
                backgroundColor:
                  pageNumber === currentPage
                    ? isProd
                      ? "#0d5da5"
                      : "#EC1117"
                    : "#f0f0f0",
                color: pageNumber === currentPage ? "#fff" : "#000",
                borderRadius: "4px",
                border: "1px solid #ccc",
                padding: "8px 12px",
                marginLeft: "5px",
                cursor: "pointer",
              }}
              onClick={() => handlePageChange(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            style={{
              backgroundColor: "#f0f0f0",
              color: "#000",
              borderRadius: "4px",
              border: "1px solid #ccc",
              padding: "8px 12px",
              marginLeft: "5px",
              cursor:
                currentPage === totalPagesCount ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPagesCount}
          >
            Next
          </button>

          <button
            style={{
              backgroundColor: "#f0f0f0",
              color: "#000",
              borderRadius: "4px",
              border: "1px solid #ccc",
              padding: "8px 12px",
              marginLeft: "5px",
              cursor:
                currentPage === totalPagesCount ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange(totalPagesCount)}
            disabled={currentPage === totalPagesCount}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default CustomDataTable;
